import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { toast } from "sonner";
import axios from "axios";
import { 
  Award, Gift, TrendingUp, Users, CheckCircle, 
  DollarSign, Share2, Star, ArrowRight, Loader2
} from "lucide-react";
import { API } from "../apiConfig";


const AmbassadorApplicationPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    socialMedia: "",
    followers: "",
    experience: "",
    motivation: ""
  });
  const [loading, setLoading] = useState(false);

  const benefits = [
    { icon: DollarSign, title: "Earn Commission", description: "Get 5% commission on every sale you refer" },
    { icon: Gift, title: "Points Rewards", description: "Earn points redeemable for store credit" },
    { icon: TrendingUp, title: "Tier Bonuses", description: "Unlock higher rewards as you grow" },
    { icon: Share2, title: "Marketing Tools", description: "QR codes, referral links, and assets" },
    { icon: Star, title: "Exclusive Access", description: "Early access to new collections" },
    { icon: Users, title: "Community", description: "Join our ambassador community" }
  ];

  const tiers = [
    { name: "Bronze", points: "0+", bonus: "1x", color: "bg-amber-600" },
    { name: "Silver", points: "5,000+", bonus: "1.1x", color: "bg-gray-400" },
    { name: "Gold", points: "15,000+", bonus: "1.25x", color: "bg-yellow-500" },
    { name: "Platinum", points: "50,000+", bonus: "1.5x", color: "bg-purple-500" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Please log in to apply");
      navigate("/login?redirect=/become-ambassador");
      return;
    }
    
    if (user.role === "promoter") {
      toast.info("You're already an ambassador!");
      navigate("/promoter");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/promoter/apply`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Application submitted! We'll review it shortly.");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero Section */}
      <section className="relative py-20 px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-background to-background" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-amber-500/30 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-orange-500/20 rounded-full blur-3xl" />
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative max-w-4xl mx-auto text-center"
        >
          <Badge className="mb-4 bg-amber-100 text-amber-800">
            <Award className="w-3 h-3 mr-1" /> Ambassador Program
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Share Aboriginal Art,{" "}
            <span className="text-amber-600">Earn Rewards</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Join our ambassador program and help share the beauty of Aboriginal art 
            while earning commissions and exclusive rewards.
          </p>
        </motion.div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 px-6 md:px-12 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-semibold text-center mb-10">Ambassador Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center mb-4">
                      <benefit.icon className="w-6 h-6 text-amber-600" />
                    </div>
                    <h3 className="font-semibold mb-2">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tier System */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold text-center mb-10">Reward Tiers</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {tiers.map((tier, idx) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className={`w-16 h-16 ${tier.color} rounded-full flex items-center justify-center mx-auto mb-3`}>
                  <Star className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-semibold">{tier.name}</h3>
                <p className="text-sm text-muted-foreground">{tier.points} points</p>
                <Badge variant="outline" className="mt-2">{tier.bonus} bonus</Badge>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 px-6 md:px-12 bg-muted/30">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center gap-2">
                <Award className="w-6 h-6 text-amber-600" />
                Apply to Become an Ambassador
              </CardTitle>
              <CardDescription>
                Tell us a bit about yourself and why you&apos;d like to join our program
              </CardDescription>
            </CardHeader>
            <CardContent>
              {user?.role === "promoter" ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">You&apos;re Already an Ambassador!</h3>
                  <p className="text-muted-foreground mb-6">
                    Access your dashboard to view your stats and start sharing.
                  </p>
                  <Button onClick={() => navigate("/promoter")} className="bg-amber-600 hover:bg-amber-700">
                    Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="socialMedia">Social Media Links</Label>
                    <Input
                      id="socialMedia"
                      placeholder="Instagram, TikTok, Facebook, etc."
                      value={formData.socialMedia}
                      onChange={(e) => setFormData(prev => ({ ...prev, socialMedia: e.target.value }))}
                    />
                    <p className="text-xs text-muted-foreground">Share your social media profiles (optional)</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="followers">Approximate Audience Size</Label>
                    <Input
                      id="followers"
                      placeholder="e.g., 500 followers, 10k community"
                      value={formData.followers}
                      onChange={(e) => setFormData(prev => ({ ...prev, followers: e.target.value }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="experience">Experience with Aboriginal Art</Label>
                    <Textarea
                      id="experience"
                      placeholder="Tell us about your connection to or interest in Aboriginal art..."
                      value={formData.experience}
                      onChange={(e) => setFormData(prev => ({ ...prev, experience: e.target.value }))}
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="motivation">Why Join Our Program?</Label>
                    <Textarea
                      id="motivation"
                      placeholder="What excites you about promoting Aboriginal art?"
                      value={formData.motivation}
                      onChange={(e) => setFormData(prev => ({ ...prev, motivation: e.target.value }))}
                      rows={3}
                      required
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    disabled={loading}
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                    ) : (
                      <>Submit Application <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>

                  {!user && (
                    <p className="text-center text-sm text-muted-foreground">
                      You&apos;ll need to{" "}
                      <a href="/login?redirect=/become-ambassador" className="text-amber-600 hover:underline">
                        log in
                      </a>{" "}
                      or{" "}
                      <a href="/register?role=promoter" className="text-amber-600 hover:underline">
                        create an account
                      </a>{" "}
                      to apply.
                    </p>
                  )}
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AmbassadorApplicationPage;
